import { setFavicons } from '../FaviconsStyles/getFavicons/index.js';
import { setTabTracking, getTabTrackingStyles } from '../TabTrackingStyles/getTabTracking/index.js';
import { getFontStyles, applyFontStyles } from '../FontsStyles/getFonts/index.js';
import { applyThemeClasses, getColorsStyles } from '../ColorStyles/getColors/index.js';
import { mergeGlobalObjects } from '../../../utils/mergeGlobalStyles/mergStyles/index.js';
import { primitiveStyles } from '../PrimitiveStyles/PrimitiveStyles/index.js';
import { resettingStyles } from '../ResetterStyles/ResetterStyles/index.js';
import { selectionStyles } from '../SelectionStyles/SelectionStyles/index.js';
import { setWritingMode } from '../WritingModeStyles/setWritingMode/index.js';
import { injectStyles } from '../../../utils/injectGlobalStyles/injectGlobalStyles/index.js';

/**
 * Generates and applies foundation styles, including fonts, themes, writing modes, and other global styles.
 *
 * This function handles both Server-Side Rendering (SSR) and Client-Side Rendering (CSR) scenarios.
 * - In SSR, it injects the necessary styles directly into the provided HTML string.
 * - In CSR, it applies styles dynamically to the document and returns cleanup functions to remove them when necessary.
 *
 * @param {Object} options - Configuration options for foundation styles.
 * @param {FontFacesType} options.fonts - Array of font definitions to be applied.
 * @param {Object} options.themes - Theme configurations to be applied.
 * @param {string} [options.writingMode='ltr'] - Writing mode direction, either 'ltr' (left-to-right) or 'rtl' (right-to-left). Defaults to 'ltr'.
 * @param {string} [ssrHtml] - Optional HTML string for SSR. If provided, styles are injected into this HTML.
 *
 * @param isMonetization
 * @returns {string|Function|null} - Returns:
 *   - A modified HTML string with injected styles in SSR.
 *   - A cleanup function to remove applied styles in CSR.
 *   - `null` if neither SSR nor CSR conditions are met.
 *
 * @example
 * // For SSR:
 * const htmlWithStyles = createFoundationStyles({ fonts, themes, writingMode }, '<html><head></head><body></body></html>');
 *
 * @example
 * // For CSR:
 * const cleanup = createFoundationStyles({ fonts, themes, writingMode });
 * // Later, to remove the styles:
 * cleanup();
 */
function createFoundationStyles({ fonts, themes, writingMode = 'ltr' }, ssrHtml, isMonetization) {
    if (ssrHtml !== undefined) {
        const htmlWithFavicons = setFavicons(ssrHtml);
        const htmlWithTabTracking = setTabTracking(htmlWithFavicons);
        const writingModeStyles = `:root { direction: ${writingMode}; }`;
        const fontStyles = getFontStyles(fonts);
        const htmlWithFonts = applyFontStyles(fonts, htmlWithTabTracking);
        const themeHtml = applyThemeClasses({ themes }, htmlWithFonts);
        const globalStyles = mergeGlobalObjects(writingModeStyles, fontStyles, getTabTrackingStyles(), selectionStyles, resettingStyles, primitiveStyles, getColorsStyles({ themes }));
        if (isMonetization) {
            return globalStyles;
        }
        return themeHtml.replace('</head>', `<style>${globalStyles}</style></head>`);
    }
    if (typeof document !== 'undefined') {
        setWritingMode(writingMode);
        const cleanupFavicons = setFavicons();
        const cleanupFonts = applyFontStyles(fonts);
        const cleanupThemes = applyThemeClasses({ themes });
        const cleanupTabTracking = setTabTracking();
        const globalStyles = mergeGlobalObjects(getTabTrackingStyles(), selectionStyles, resettingStyles, primitiveStyles, getColorsStyles({ themes }), getFontStyles(fonts));
        injectStyles(globalStyles);
        return () => {
            cleanupFavicons();
            cleanupFonts();
            cleanupThemes();
            cleanupTabTracking();
            const injectedStyles = document.head.querySelector('style[ds-foundation]');
            if (injectedStyles)
                injectedStyles.remove();
        };
    }
    return null;
}

export { createFoundationStyles };
